import { useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useStudentData } from "@/context";
import { useGoNoAccess } from "@hooks/useGoNoAccess";
import { getCurrentSectionFromPath } from "@utils/lessonUtils";
import { getBaseRoute, getLessonRoute } from "@utils/route";
import { REQUEST_STATUS } from "@ts/enums/RequestStatus";
import { LESSON_STATUS, TERM_STATUS } from "@ts/enums/CompletionStatus";

/**
 * Enforce lesson access rules and redirects:
 *  - If week lessons fetch failed (403) → redirect to no-access
 *  - If lesson is "disabled" → redirect to not-found
 *  - If no lesson slug in url → navigate to 404
 *  - If lesson is "completed" and user is on the base lesson page → redirect to /summary
 *  - If any term is "started" and user is on the base lesson page → redirect to /introduction
 */
const useLessonEntryGuard = () => {
  const navigate = useNavigate();
  const navigateToNoAccess = useGoNoAccess();
  const { pathname } = useLocation();
  const { lessonSlug } = useParams<{
    lessonSlug?: string;
  }>();

  const {
    selectedGrade,
    terms,
    selectedTerm,
    selectedWeek,
    termsStatus,
    weekLessonsStatus,
    selectedLesson,
    selectedSubject
  } = useStudentData();

  const currentSection = getCurrentSectionFromPath(pathname);
  const isOnBasePage = /^\d+$/.test(currentSection);

  // "started" OR "completed" — a student who finished lessons has active terms
  // even if none are literally "started" any more (PLAN-309 redirect loop fix).
  const hasAnyTermActive = useMemo(
    () =>
      Array.isArray(terms) &&
      terms.some(t => t.status === TERM_STATUS.STARTED || t.status === TERM_STATUS.COMPLETED),
    [terms]
  );

  // PLAN-310: the terms-summary fetch cycles IDLE → PENDING → SUCCESS while
  // `terms` stays at the previous value (or initial `[]`). Wait for SUCCESS
  // so the redirect logic only ever acts on confirmed data.
  const termsAreLoaded = termsStatus === REQUEST_STATUS.SUCCESS;

  // If week lessons fetch failed (e.g. 403 — no licence), redirect to no-access
  useEffect(() => {
    if (weekLessonsStatus === REQUEST_STATUS.ERROR) {
      navigateToNoAccess();
    }
  }, [weekLessonsStatus, navigateToNoAccess]);

  useEffect(() => {
    if (!lessonSlug) {
      navigate(`${getBaseRoute()}not-found`, { replace: true });
    }
  }, [lessonSlug, navigate]);

  // If lesson is disabled, redirect to not-found
  useEffect(() => {
    if (selectedLesson?.status === LESSON_STATUS.DISABLED) {
      navigate(`${getBaseRoute()}not-found`, { replace: true });
    }
  }, [selectedLesson?.status, navigate]);

  useEffect(() => {
    // wait until URL-driven state is hydrated
    if (
      !selectedGrade ||
      !selectedTerm ||
      !selectedWeek ||
      !selectedLesson ||
      !selectedSubject
    ) {
      return;
    }

    const introUrl = getLessonRoute(
      selectedGrade.grade_code,
      selectedTerm.id,
      selectedWeek.id,
      selectedSubject.slug,
      String(selectedLesson.id),
      "introduction"
    );

    const summaryUrl = getLessonRoute(
      selectedGrade.grade_code,
      selectedTerm.id,
      selectedWeek.id,
      selectedSubject.slug,
      String(selectedLesson.id),
      "summary"
    );

    const normalize = (s: string) => s.replace(/\/+$/u, "");
    const onIntro = normalize(pathname) === normalize(introUrl);
    const onSummary = normalize(pathname) === normalize(summaryUrl);

    // 1) Lesson completed + user on base lesson page -> go to Summary
    //    (Status comes from selectedLesson, not terms, so safe to act without
    //    termsAreLoaded.)
    if (selectedLesson.status === LESSON_STATUS.COMPLETED && isOnBasePage && !onSummary) {
      navigate(summaryUrl, { replace: true });
      return;
    }

    // Rule 2 depends on hasAnyTermActive. Skip if terms haven't confirmed yet
    // — see termsAreLoaded comment above.
    if (!termsAreLoaded) return;

    // 2) Any term started + user on lesson base page → go to /introduction
    if (hasAnyTermActive && isOnBasePage && !onIntro) {
      navigate(introUrl, { replace: true });
    }
  }, [
    pathname,
    termsAreLoaded,
    hasAnyTermActive,
    isOnBasePage,
    selectedGrade,
    selectedTerm,
    selectedWeek,
    selectedLesson,
    selectedSubject,
    navigate
  ]);
};

export default useLessonEntryGuard;
